<ng-template
  #statusDropdown="rtConnectedOverlay"
  rt-connected-overlay
  [hasBackdrop]="true"
  [origin]="statusDropdownOrigin"
  [rtConnectedOverlayPosition]="[OverlayPosition.BottomLeft, OverlayPosition.TopLeft]"
  [width]="200"
  (backdropClick)="statusDropdown.open = false"
>
  <rt-dropdown>
    @for (status of ticketStatuses; track status) {
      <rt-dropdown-item (click)="statusChanged.emit(status)">
        <rt-icon [name]="statusIconMap[status]" [colorToken]="statusIconColorMap[status]" prefix />
        {{ statusTranslations[status] }}
      </rt-dropdown-item>
    }
  </rt-dropdown>
</ng-template>

<ng-template
  #priorityDropdown="rtConnectedOverlay"
  rt-connected-overlay
  [hasBackdrop]="true"
  [origin]="priorityDropdownOrigin"
  [rtConnectedOverlayPosition]="[OverlayPosition.BottomLeft, OverlayPosition.TopLeft]"
  [width]="200"
  (backdropClick)="priorityDropdown.open = false"
>
  <rt-dropdown>
    @for (priority of priorityDropdownItems; track priority.key) {
      <rt-dropdown-item (click)="priorityChanged.emit(priority.key)">
        <rt-icon name="flag" [colorToken]="priority.color" prefix />
        {{ priority.value }}
      </rt-dropdown-item>
    }
  </rt-dropdown>
</ng-template>

<div class="rt-card ticket-card">
  <div class="row">
    <div class="label">
      <rt-icon name="radio-button-checked" margin-right-small-x />
      <span>{{ 'tlc.ask_hr.request_dialog.status' | translate }}</span>
    </div>
    <div class="value">
      <rt-button
        #statusDropdownOrigin="rtOverlayOrigin"
        rt-overlay-origin
        variant="primary"
        size="small"
        [style.background-color]="tokenToCssVar | fnCall: statusColorMap[ticket().status]"
        [dropdownOpened]="statusDropdown.open"
        [dropdownVisible]="canChangeStatus()"
        [class.disabled]="!canChangeStatus()"
        (click)="canChangeStatus() && (statusDropdown.open = true)"
      >
        <rt-icon
          [name]="statusIconMap[ticket().status]"
          colorToken="colorIconOnprimaryDefault"
          margin-right-small-x
        />
        {{ statusTranslations[ticket().status] }}
      </rt-button>
    </div>
    @if (isCancelVisible()) {
      <div
        [rtTooltip]="'tlc.ask_hr.request_dialog.cancel_ticket_tooltip' | translate"
        [rtTooltipEnabled]="!canCancel()"
        rtTooltipPosition="left"
        margin-left-auto
      >
        <rt-button
          variant="plain"
          size="small"
          [disabled]="!canCancel()"
          (click)="cancelClick.emit()"
        >
          {{ 'tlc.ask_hr.request_dialog.cancel_ticket' | translate }}
        </rt-button>
      </div>
    }
  </div>
  <div class="row">
    <div class="label">
      <rt-icon name="flag" margin-right-small-x />
      <span>{{ 'tlc.ask_hr.requests.priority' | translate }}</span>
    </div>
    <div class="value">
      <rt-button
        #priorityDropdownOrigin="rtOverlayOrigin"
        rt-overlay-origin
        variant="plain"
        size="small"
        [dropdownOpened]="priorityDropdown.open"
        [dropdownVisible]="canChangePriority()"
        [class.disabled]="!canChangePriority()"
        (click)="canChangePriority() && (priorityDropdown.open = true)"
      >
        @if (ticket().priority; as priority) {
          <rt-icon name="flag" [colorToken]="priorityColorMap[priority]" margin-right-small-x />
          {{ priorityTranslations[priority] }}
        } @else {
          -
        }
      </rt-button>
    </div>
  </div>
  <div class="row">
    <div class="label">
      <rt-icon name="person" margin-right-small-x />
      <span>{{ 'tlc.ask_hr.request_dialog.submitted_by' | translate }}</span>
    </div>
    <div class="value">
      <rt-avatar
        [initials]="requesterInitials()"
        [imageSrc]="ticket().requester.avatarThumbUrl"
        [type]="ticket().requester.avatarThumbUrl ? 'image' : 'initials'"
        size="small-x"
        margin-right-small-x
      />
      {{ ticket().requester.firstName }} {{ ticket().requester.lastName }}
    </div>
  </div>
  <div class="row">
    <div class="label">
      <rt-icon name="assignment" margin-right-small-x />
      <span>{{ 'tlc.ask_hr.requests.assigned_to' | translate }}</span>
    </div>
    <div
      class="value assignees"
      [attr.padding-top-small-x]="ticket().assignees.length > 1 ? '' : null"
    >
      <ng-template
        #assigneesTrigger
        let-select
        let-inOverlay="inOverlay"
        [ngTemplateType]="MembersSelectType"
      >
        @let items = select.selectController.itemSelector.selected();
        <div [class.assignees-trigger-hidden]="inOverlay">
          @for (item of items || []; track item.value) {
            <div
              layout
              layout-align-items-center
              class="assignees-trigger"
              margin-bottom-small-xx
              (click)="$first && canChangeAssignees() && select.selectController.toggle(true)"
            >
              @if (assertMemberSelectItem(item)) {
                <rt-avatar
                  [initials]="item.initials"
                  [imageSrc]="item.avatarUrl"
                  [type]="item.avatarUrl ? 'image' : 'initials'"
                  size="small-x"
                  margin-right-small-x
                />
                {{ item.label }}
              }
              @if ($first && canChangeAssignees()) {
                <rt-icon name="arrow-drop-down" margin-left-small-x />
              }
            </div>
          } @empty {
            <div
              layout
              layout-align-items-center
              class="assignees-trigger"
              (click)="canChangeAssignees() && select.selectController.toggle(true)"
            >
              <span>{{ 'tlc.ask_hr.request_dialog.unassigned' | translate }}</span>
              @if (canChangeAssignees()) {
                <rt-icon name="arrow-drop-down" margin-left-small-x />
              }
            </div>
          }
        </div>
      </ng-template>

      <team-members-multiselect
        [control]="assigneesControl"
        [selectedMembers]="ticket().assignees"
        [trigger]="assigneesTrigger"
        [overlayWidth]="300"
        (selectedMembersChange)="onAssigneesChange($event)"
        selectorScope="ask-hr-ticket"
        layout-full-width
      />
    </div>
  </div>
  <div class="row">
    <div class="label">
      <rt-icon name="calendar-today" margin-right-small-x />
      <span>{{ 'tlc.ask_hr.request_dialog.created_at' | translate }}</span>
    </div>
    <div class="value">
      {{ ticket().createdAt | amDateFormat: 'DD MMM YYYY' }}
    </div>
  </div>
</div>
